/**
 * Spec 095: three-tile KPI row above the existing CollectionStatus block
 * on the Collection page. Pure presentational; the data comes from the
 * `/collection/preview` brand-level breakdown.
 *
 * Tiles:
 *   - Eligible now: jobs ready to drain on the next cron tick
 *   - Scheduled this week: jobs intentionally deferred per CM-4 staggering
 *   - Stuck-running recovered (24h): jobs the auto-recovery rescued recently;
 *     reads 0 in steady state. Non-zero is a signal worth investigating.
 *
 * Spec 231: this used to be three standalone rounded/bordered tiles — a
 * small card-in-a-tray of its own. It now renders through the shared
 * StatRow/Stat primitives so it reads as one instrument, matching the rest
 * of the dashboard archetype. Every testid is unchanged — the contract this
 * component makes with its callers and with the gates that assert on it is
 * the testids, not the markup.
 */
import { StatRow, Stat } from './ui/StatRow.js';

interface CollectionKpiTilesProps {
  eligibleNow: number;
  scheduledLater: number;
  recovered24h: number;
}

interface TileProps {
  label: string;
  /** Spec 248: what the count measures, under the label. */
  note: string;
  icon: string;
  value: number;
  testId: string;
}

/**
 * Spec 248 (review P1-5): the review found these as "three cells, a label and
 * a zero" — a number with nothing to say what it counts. Each now has Kridt's
 * anatomy: disc, label, note, figure.
 *
 * The recovered count used to turn TEAL when non-zero, deliberately not the
 * danger colour since a successful recovery is not bad news. The reasoning
 * holds; the mechanism doesn't: Kridt puts tone in the note, never on the
 * number. A non-zero recovery count is its own signal, and the note says what
 * it means.
 */
function Tile({ label, note, icon, value, testId }: TileProps) {
  return <Stat icon={icon} label={label} note={note} value={value} data-testid={testId} />;
}

export function CollectionKpiTiles({
  eligibleNow,
  scheduledLater,
  recovered24h,
}: CollectionKpiTilesProps) {
  return (
    <StatRow data-testid="collection-kpi-tiles">
      <Tile
        icon="bx-play-circle"
        label="Eligible now"
        note="Ready for the next collection run"
        value={eligibleNow}
        testId="kpi-tile-eligible-now"
      />
      <Tile
        icon="bx-calendar"
        label="Scheduled this week"
        note="Spread across seven days by design"
        value={scheduledLater}
        testId="kpi-tile-scheduled-later"
      />
      <Tile
        icon="bx-revision"
        label="Recovered (24h)"
        note={recovered24h > 0 ? 'Rescued after getting stuck — worth a look' : 'Jobs rescued after getting stuck'}
        value={recovered24h}
        testId="kpi-tile-recovered"
      />
    </StatRow>
  );
}
